import * as THREE from 'three';
import { createNameLabel } from '../utils/NameLabel.js';

export class ChoomaAlly {
    constructor(scene, position) {
        this.scene = scene;
        this.health = 200; // Big hit count!
        this.maxHealth = 200;
        this.speed = 4;
        this.attackRange = 2.5; // Melee range!
        this.attackDamage = 30; // ~50% of sasquatch HP (60)
        this.lastAttackTime = 0;
        this.attackCooldown = 1.5;
        this.isAttacking = false;
        this.attackStartTime = 0;
        this.attackDuration = 0.5; // Attack animation duration
        this.target = null;
        this.isDead = false;
        this.lastVoiceTime = 0;
        this.voiceCooldown = 15; // Say things every 15 seconds (reduced spam)
        
        // Random jumping behavior
        this.lastJumpTime = Date.now() / 1000;
        this.nextJumpDelay = this.generateJumpDelay(); // Normal distribution
        
        // Random FLINGING behavior (new!)
        this.isFlinging = false;
        this.flingVelocity = new THREE.Vector3();
        this.flingStartTime = 0;
        this.flingDuration = 0.5; // 0.5 seconds to reach max speed
        this.flingMaxSpeed = 0;
        this.lastFlingTime = Date.now() / 1000;
        this.nextFlingDelay = this.generateFlingDelay();
        
        // Random target forgetting (up to 1 minute)
        this.forgetTarget = false;
        this.forgetUntilTime = 0;
        this.lastForgetTime = Date.now() / 1000;
        this.nextForgetDelay = this.generateForgetDelay();
        
        // Random spinning behavior
        this.isSpinning = false;
        this.spinStartTime = 0;
        this.spinDuration = 0;
        this.spinSpeed = 0;
        this.lastSpinTime = Date.now() / 1000;
        this.nextSpinDelay = this.generateSpinDelay();
        
        // Random RUNNING behavior - run in random direction for 1s every 4s
        this.isRandomRunning = false;
        this.randomRunDirection = new THREE.Vector3();
        this.randomRunStartTime = 0;
        this.randomRunDuration = 1; // 1 second
        this.lastRandomRunTime = Date.now() / 1000;
        this.randomRunCooldown = 4; // Every 4 seconds

        this.createMesh(position);
        this.createHealthBar();
    }
    
    // Generate jump delay using Box-Muller transform for normal distribution
    // Mean: 90 seconds (1.5 min), SD: 14 seconds
    generateJumpDelay() {
        const u1 = Math.random();
        const u2 = Math.random();
        const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const delay = 90 + z * 14; // mean 90s, sd 14s
        return Math.max(10, Math.min(180, delay)); // Clamp between 10-180 seconds
    }
    
    // Generate fling delay - mean 30s, sd 10s
    generateFlingDelay() {
        const u1 = Math.random();
        const u2 = Math.random();
        const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const delay = 30 + z * 10; // mean 30s, sd 10s
        return Math.max(5, Math.min(60, delay)); // Clamp between 5-60 seconds
    }
    
    // Generate forget delay - mean 45s, sd 15s
    generateForgetDelay() {
        const u1 = Math.random();
        const u2 = Math.random();
        const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const delay = 45 + z * 15; // mean 45s, sd 15s
        return Math.max(10, Math.min(120, delay)); // Clamp between 10-120 seconds
    }
    
    // Generate spin delay - mean 40s, sd 12s
    generateSpinDelay() {
        const u1 = Math.random();
        const u2 = Math.random();
        const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const delay = 40 + z * 12; // mean 40s, sd 12s
        return Math.max(15, Math.min(90, delay)); // Clamp between 15-90 seconds
    }
    
    createMesh(position) {
        // Create Chooma - mushroom character with body and cap
        const choomaGroup = new THREE.Group();
        
        // BODY - pill/oval shaped (yellow blob)
        const bodyGeometry = new THREE.SphereGeometry(0.4, 16, 16);
        const bodyMaterial = new THREE.MeshStandardMaterial({
            color: 0xffdd00,
            emissive: 0xffdd00,
            emissiveIntensity: 0.3,
            roughness: 0.5,
            metalness: 0.2
        });
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
        body.scale.set(0.8, 1.2, 0.8); // Make it pill-shaped
        body.position.y = 0.3;
        body.castShadow = true;
        choomaGroup.add(body);
        
        // No mushroom cap - just the yellow body!
        
        choomaGroup.position.copy(position);
        this.scene.add(choomaGroup);
        this.mesh = choomaGroup;
        
        // White eyes with black outline and dot
        // Left eye white part
        const eyeWhiteGeometry = new THREE.SphereGeometry(0.08, 8, 8);
        const eyeWhiteMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
        const leftEyeWhite = new THREE.Mesh(eyeWhiteGeometry, eyeWhiteMaterial);
        leftEyeWhite.position.set(-0.12, 0.4, 0.32);
        body.add(leftEyeWhite);
        
        // Left eye black outline (slightly larger black sphere behind)
        const eyeOutlineGeometry = new THREE.SphereGeometry(0.085, 8, 8);
        const eyeOutlineMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
        const leftEyeOutline = new THREE.Mesh(eyeOutlineGeometry, eyeOutlineMaterial);
        leftEyeOutline.position.set(-0.12, 0.4, 0.315);
        body.add(leftEyeOutline);
        
        // Left pupil (small black dot)
        const pupilGeometry = new THREE.SphereGeometry(0.03, 8, 8);
        const pupilMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
        const leftPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
        leftPupil.position.set(-0.12, 0.4, 0.33);
        body.add(leftPupil);
        
        // Right eye white part
        const rightEyeWhite = new THREE.Mesh(eyeWhiteGeometry, eyeWhiteMaterial);
        rightEyeWhite.position.set(0.12, 0.4, 0.32);
        body.add(rightEyeWhite);
        
        // Right eye black outline
        const rightEyeOutline = new THREE.Mesh(eyeOutlineGeometry, eyeOutlineMaterial);
        rightEyeOutline.position.set(0.12, 0.4, 0.315);
        body.add(rightEyeOutline);
        
        // Right pupil
        const rightPupil = new THREE.Mesh(pupilGeometry, pupilMaterial);
        rightPupil.position.set(0.12, 0.4, 0.33);
        body.add(rightPupil);
        
        // Simple mouth (vertical line)
        const mouthGeometry = new THREE.BoxGeometry(0.02, 0.15, 0.02);
        const mouthMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
        const mouth = new THREE.Mesh(mouthGeometry, mouthMaterial);
        mouth.position.set(0, 0.25, 0.32);
        body.add(mouth);
        
        // Stubby arms
        const armGeometry = new THREE.BoxGeometry(0.15, 0.1, 0.1);
        const armMaterial = new THREE.MeshStandardMaterial({
            color: 0xffdd00,
            emissive: 0xffdd00,
            emissiveIntensity: 0.2
        });
        
        const leftArm = new THREE.Mesh(armGeometry, armMaterial);
        leftArm.position.set(-0.35, 0.3, 0);
        leftArm.rotation.z = -0.3;
        choomaGroup.add(leftArm);
        
        const rightArm = new THREE.Mesh(armGeometry, armMaterial);
        rightArm.position.set(0.35, 0.3, 0);
        rightArm.rotation.z = 0.3;
        choomaGroup.add(rightArm);

        // Add name label
        const label = createNameLabel('CHOOMA', '#ffff00');
        this.mesh.add(label);
    }
    
    findNearestEnemy(enemies) {
        let nearest = null;
        let nearestDistance = Infinity;
        
        enemies.forEach(enemy => {
            const distance = this.mesh.position.distanceTo(enemy.mesh.position);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearest = enemy;
            }
        });
        
        return nearest;
    }
    
    attack(targetPosition) {
        const currentTime = Date.now() / 1000;
        if (currentTime - this.lastAttackTime < this.attackCooldown) return;
        if (this.isAttacking) return;
        
        this.lastAttackTime = currentTime;
        this.isAttacking = true;
        this.attackStartTime = currentTime;
        
        // MELEE ATTACK - lunge forward!
        const direction = new THREE.Vector3();
        direction.subVectors(targetPosition, this.mesh.position);
        direction.normalize();
        
        // Lunge forward quickly
        this.mesh.position.add(direction.multiplyScalar(1.5));
        
        // Deal damage to target if in range
        if (this.target && !this.target.isDead) {
            const distance = this.mesh.position.distanceTo(this.target.mesh.position);
            if (distance < this.attackRange * 2) {
                // Damage the enemy
                this.target.health -= this.attackDamage;
                
                // KNOCKBACK: Send chooma BACK spinning wildly (10x walk speed)
                const knockbackDirection = direction.clone().multiplyScalar(-1); // Opposite of attack
                this.knockbackVelocity = knockbackDirection.multiplyScalar(40); // 10x walk speed (4 * 10 = 40)
                this.knockbackDuration = 0.3; // Knockback for 0.3 seconds
                this.knockbackTime = currentTime;
                
                // Random spinning on all axes
                this.spinVelocity = new THREE.Vector3(
                    (Math.random() - 0.5) * 30, // Random X spin
                    (Math.random() - 0.5) * 30, // Random Y spin
                    (Math.random() - 0.5) * 30  // Random Z spin
                );
                
                // KNOCKBACK SASQUATCH: Push enemy back slightly in same direction
                if (this.target.mesh) {
                    this.target.mesh.position.add(direction.clone().multiplyScalar(0.5)); // Push back
                    
                    // Make sasquatch off-kilter (tilt)
                    this.target.mesh.rotation.x += (Math.random() - 0.5) * 0.3;
                    this.target.mesh.rotation.z += (Math.random() - 0.5) * 0.3;
                }
                
                // Play hit sound
                if (window.game && window.game.audioManager) {
                    window.game.audioManager.playHit();
                }
                
                if (this.target.health <= 0 && !this.target.isDead) {
                    this.target.isDead = true;
                    // Cleanup handled in main.js collision detection
                }
            }
        }
        
        // Play rising scream: "ooooooooeoeeeeeeeOOOOEOEAAAAAAAARRRRR!"
        this.playAttackScream();
        
        // Reset attack after duration
        setTimeout(() => {
            this.isAttacking = false;
        }, this.attackDuration * 1000);
    }
    
    playAttackScream() {
        if (!window.game || !window.game.audioManager || !window.game.audioManager.initialized) return;
        
        const audioManager = window.game.audioManager;
        const context = audioManager.context;
        
        // Create oscillator for scream
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        const filter = context.createBiquadFilter();
        
        // Create 3D panner for positional audio
        const panner = context.createPanner();
        panner.panningModel = 'HRTF';
        panner.distanceModel = 'inverse';
        panner.refDistance = 5;
        panner.maxDistance = 100;
        panner.rolloffFactor = 1;
        
        if (panner.positionX) {
            panner.positionX.value = this.mesh.position.x;
            panner.positionY.value = this.mesh.position.y;
            panner.positionZ.value = this.mesh.position.z;
        }
        
        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(panner);
        panner.connect(audioManager.sfxGain);
        
        // Sawtooth wave for aggressive sound
        oscillator.type = 'sawtooth';
        
        // Start LOW (C2 = 65.41 Hz) and rise to HIGH (C5 = 523.25 Hz) - 3 octaves!
        const startFreq = 65.41;  // C2
        const endFreq = 523.25;   // C5 - whole extra octave!
        const duration = 1.5; // 1.5 second scream
        
        // Frequency rises exponentially (dramatic!)
        oscillator.frequency.setValueAtTime(startFreq, context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(endFreq, context.currentTime + duration);
        
        // Filter sweeps up too for more dramatic effect
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(200, context.currentTime);
        filter.frequency.exponentialRampToValueAtTime(2000, context.currentTime + duration);
        filter.Q.setValueAtTime(3, context.currentTime);
        
        // Volume envelope: quick attack, sustain, then fade
        gainNode.gain.setValueAtTime(0, context.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.3, context.currentTime + 0.05); // Attack
        gainNode.gain.setValueAtTime(0.3, context.currentTime + 0.8); // Sustain
        gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + duration); // Fade
        
        oscillator.start(context.currentTime);
        oscillator.stop(context.currentTime + duration);
    }
    
    update(deltaTime, enemies, otherChoomas = []) {
        if (this.isDead) return;
        
        const currentTime = Date.now() / 1000;
        
        // Handle knockback from attack (takes priority over all other movement)
        if (this.knockbackVelocity && currentTime - this.knockbackTime < this.knockbackDuration) {
            // Apply knockback movement
            this.mesh.position.add(this.knockbackVelocity.clone().multiplyScalar(deltaTime));
            
            // Apply wild spinning
            if (this.spinVelocity) {
                this.mesh.rotation.x += this.spinVelocity.x * deltaTime;
                this.mesh.rotation.y += this.spinVelocity.y * deltaTime;
                this.mesh.rotation.z += this.spinVelocity.z * deltaTime;
            }
            
            // Skip normal behavior during knockback
            return;
        } else if (this.knockbackVelocity) {
            // Knockback finished - reset
            this.knockbackVelocity = null;
            this.spinVelocity = null;
            // Reset rotation to upright
            this.mesh.rotation.x = 0;
            this.mesh.rotation.z = 0;
        }
        
        // Update health bar
        this.updateHealthBar();
        
        // Randomly speak (REDUCED to prevent audio spam)
        if (Math.random() < 0.002) { // 0.2% chance per frame
            this.speak();
        }
        
        // Random jumping with normal distribution timing
        if (currentTime - this.lastJumpTime > this.nextJumpDelay) {
            this.randomJump();
            this.lastJumpTime = currentTime;
            this.nextJumpDelay = this.generateJumpDelay();
        }
        
        // Random target forgetting (up to 1 minute)
        if (currentTime > this.forgetUntilTime && currentTime - this.lastForgetTime > this.nextForgetDelay) {
            const forgetDuration = 10 + Math.random() * 50; // 10-60 seconds
            this.forgetUntilTime = currentTime + forgetDuration;
            this.forgetTarget = true;
            this.lastForgetTime = currentTime;
            this.nextForgetDelay = this.generateForgetDelay();
        }
        
        // Clear forget flag when time expires
        if (currentTime > this.forgetUntilTime) {
            this.forgetTarget = false;
        }
        
        // Random spinning behavior
        if (!this.isSpinning && currentTime - this.lastSpinTime > this.nextSpinDelay) {
            this.startSpinning();
        }
        
        // Handle spinning
        if (this.isSpinning) {
            const spinElapsed = currentTime - this.spinStartTime;
            if (spinElapsed < this.spinDuration) {
                this.mesh.rotation.y += this.spinSpeed * deltaTime;
            } else {
                this.isSpinning = false;
                this.lastSpinTime = currentTime;
                this.nextSpinDelay = this.generateSpinDelay();
            }
        }
        
        // Random FLINGING with 5-15x speed!
        if (!this.isFlinging && currentTime - this.lastFlingTime > this.nextFlingDelay) {
            this.startFling();
        }
        
        // Handle flinging movement
        if (this.isFlinging) {
            const flingElapsed = currentTime - this.flingStartTime;
            
            if (flingElapsed < this.flingDuration) {
                // Linear acceleration to max speed over 0.5 seconds
                const progress = flingElapsed / this.flingDuration;
                const currentSpeed = this.flingMaxSpeed * progress;
                
                // Move in fling direction
                const flingMovement = this.flingVelocity.clone().multiplyScalar(currentSpeed * deltaTime);
                this.mesh.position.add(flingMovement);
                
                // Visual: spin while flinging!
                this.mesh.rotation.y += deltaTime * 20;
                
                // VOLCANO COLLISION during fling - bounce off!
                const distFromVolcano = Math.sqrt(this.mesh.position.x ** 2 + this.mesh.position.z ** 2);
                if (distFromVolcano < 22) {
                    // Push away from volcano and stop fling
                    const awayFromVolcano = new THREE.Vector3(this.mesh.position.x, 0, this.mesh.position.z);
                    awayFromVolcano.normalize();
                    this.mesh.position.x = awayFromVolcano.x * 22;
                    this.mesh.position.z = awayFromVolcano.z * 22;
                    // End fling early (bounced off volcano!)
                    this.isFlinging = false;
                    this.mesh.rotation.y = 0;
                }
            } else {
                // Stop flinging
                this.isFlinging = false;
                this.lastFlingTime = currentTime;
                this.nextFlingDelay = this.generateFlingDelay();
                this.mesh.rotation.y = 0; // Reset rotation
            }
            
            // Skip normal movement when flinging
            return;
        }
        
        // Random RUNNING behavior - run in random direction for 1s every 4s
        if (!this.isRandomRunning && currentTime - this.lastRandomRunTime > this.randomRunCooldown) {
            this.isRandomRunning = true;
            this.randomRunStartTime = currentTime;
            // Pick random direction
            const angle = Math.random() * Math.PI * 2;
            this.randomRunDirection.set(Math.cos(angle), 0, Math.sin(angle));
            this.randomRunDirection.normalize();
        }
        
        // Handle random running
        if (this.isRandomRunning) {
            const runElapsed = currentTime - this.randomRunStartTime;
            if (runElapsed < this.randomRunDuration) {
                // Run at full speed in random direction
                this.mesh.position.add(this.randomRunDirection.clone().multiplyScalar(this.speed * deltaTime));
                
                // Visual: slight bobbing while running
                this.mesh.position.y = 1 + Math.sin(currentTime * 10) * 0.1;
                
                // Skip normal behavior during random run
                return;
            } else {
                // Stop random running
                this.isRandomRunning = false;
                this.lastRandomRunTime = currentTime;
            }
        }
        
        // Find nearest enemy to attack (unless forgetting target)
        if (!this.forgetTarget) {
            this.target = this.findNearestEnemy(enemies);
        } else {
            this.target = null; // Forgot target!
        }
        
        if (this.target) {
            const targetPos = this.target.mesh.position;
            const direction = new THREE.Vector3();
            direction.subVectors(targetPos, this.mesh.position);
            const distance = direction.length();
            direction.normalize();
            
            // Check collision with other Choomas
            const avoidanceVector = new THREE.Vector3();
            let nearbyCount = 0;
            otherChoomas.forEach(other => {
                if (other !== this && !other.isDead) {
                    const dist = this.mesh.position.distanceTo(other.mesh.position);
                    if (dist < 3) { // Collision distance
                        const away = new THREE.Vector3();
                        away.subVectors(this.mesh.position, other.mesh.position);
                        away.normalize();
                        avoidanceVector.add(away);
                        nearbyCount++;
                    }
                }
            });
            
            // Avoid grouping more than 2
            if (nearbyCount > 1) {
                avoidanceVector.normalize();
                this.mesh.position.add(avoidanceVector.multiplyScalar(this.speed * deltaTime * 0.5));
            } else {
                // Move towards enemy if too far (get close for melee!)
                if (distance > this.attackRange) {
                    this.mesh.position.add(direction.multiplyScalar(this.speed * deltaTime));
                }
                
                // Visual attack animation - grow slightly when attacking
                if (this.isAttacking) {
                    const attackProgress = (Date.now() / 1000 - this.attackStartTime) / this.attackDuration;
                    const scale = 1 + Math.sin(attackProgress * Math.PI) * 0.3; // Pulse during attack
                    this.mesh.scale.set(scale, scale, scale);
                } else {
                    this.mesh.scale.set(1, 1, 1);
                }
            }
            
            // VOLCANO COLLISION - don't enter volcano (radius 22)
            const distFromVolcano = Math.sqrt(this.mesh.position.x ** 2 + this.mesh.position.z ** 2);
            if (distFromVolcano < 22) {
                // Push away from volcano center
                const awayFromVolcano = new THREE.Vector3(this.mesh.position.x, 0, this.mesh.position.z);
                awayFromVolcano.normalize();
                this.mesh.position.x = awayFromVolcano.x * 22;
                this.mesh.position.z = awayFromVolcano.z * 22;
            }
            
            // Look at target
            this.mesh.lookAt(targetPos);
            
            // Attack if in range
            if (distance < this.attackRange) {
                this.attack(targetPos);
            }
        } else {
            // Idle behavior - bob up and down
            this.mesh.position.y = 1 + Math.sin(Date.now() / 500) * 0.2;
        }
        
        // No projectiles anymore - melee attacks only!
    }
    
    createHealthBar() {
        // Create health bar above Chooma
        const barWidth = 1;
        const barHeight = 0.1;
        
        const barGeometry = new THREE.PlaneGeometry(barWidth, barHeight);
        const barBackMaterial = new THREE.MeshBasicMaterial({ color: 0x333333 });
        this.healthBarBack = new THREE.Mesh(barGeometry, barBackMaterial);
        this.healthBarBack.position.y = 1.8;
        this.mesh.add(this.healthBarBack);
        
        const healthGeometry = new THREE.PlaneGeometry(barWidth, barHeight);
        const healthMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
        this.healthBarFill = new THREE.Mesh(healthGeometry, healthMaterial);
        this.healthBarFill.position.z = 0.01;
        this.healthBarBack.add(this.healthBarFill);
    }
    
    updateHealthBar() {
        if (this.healthBarFill) {
            const healthPercent = this.health / this.maxHealth;
            this.healthBarFill.scale.x = healthPercent;
            this.healthBarFill.position.x = -(1 - healthPercent) / 2;
            
            // Make health bar always face camera
            if (window.game && window.game.camera) {
                this.healthBarBack.lookAt(window.game.camera.position);
            }
        }
    }
    
    speak() {
        const currentTime = Date.now() / 1000;
        if (currentTime - this.lastVoiceTime < this.voiceCooldown) return;
        
        this.lastVoiceTime = currentTime;
        
        // Play actual voice file with positional audio
        if (window.game && window.game.audioManager) {
            window.game.audioManager.playVoice('chooma', this.mesh.position);
        }
    }
    
    startFling() {
        // Fling in random direction at 5-15x normal speed!
        const speedMultiplier = 5 + Math.random() * 10; // 5-15x
        this.flingMaxSpeed = this.speed * speedMultiplier;
        
        // Random direction
        const angle = Math.random() * Math.PI * 2;
        this.flingVelocity = new THREE.Vector3(
            Math.cos(angle),
            0,
            Math.sin(angle)
        ).normalize();
        
        this.isFlinging = true;
        this.flingStartTime = Date.now() / 1000;
    }
    
    startSpinning() {
        // Random spinning - duration 1-3 seconds, speed 5-15 rad/sec
        this.spinDuration = 1 + Math.random() * 2; // 1-3 seconds
        this.spinSpeed = 5 + Math.random() * 10; // 5-15 rad/sec
        this.isSpinning = true;
        this.spinStartTime = Date.now() / 1000;
    }
    
    randomJump() {
        // Generate random distance with normal distribution (20-100 meters)
        const u1 = Math.random();
        const u2 = Math.random();
        const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
        const distance = 60 + z * 20; // mean 60m, sd 20m
        const clampedDistance = Math.max(20, Math.min(100, distance));
        
        // Random direction
        const angle = Math.random() * Math.PI * 2;
        
        // Calculate new position
        const newX = this.mesh.position.x + Math.cos(angle) * clampedDistance;
        const newZ = this.mesh.position.z + Math.sin(angle) * clampedDistance;
        
        // Make sure we don't jump too far from center (stay on island)
        const distFromCenter = Math.sqrt(newX * newX + newZ * newZ);
        if (distFromCenter > 130) {
            return; // Don't jump off island
        }
        
        // Teleport to new location
        this.mesh.position.x = newX;
        this.mesh.position.z = newZ;
        
        // CRITICAL: Set Y to ground height to prevent floating
        const groundHeight = this.getTerrainHeight(this.mesh.position);
        this.mesh.position.y = groundHeight;
        
        // Random spin
        this.mesh.rotation.y = Math.random() * Math.PI * 2;
    }
    
    takeDamage(amount) {
        this.health -= amount;
        this.updateHealthBar();
        
        if (this.health <= 0 && !this.isDead) {
            this.isDead = true;
        }
    }
    
    destroy() {
        this.scene.remove(this.mesh);
        this.projectiles.forEach(p => this.scene.remove(p));
    }
}
